"use client";

import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { EASE } from "@/lib/motion";
import { useVerifyIdentityAction } from "@/lib/kyc/hooks";
import { Icon } from "@/components/ui/Icon";
import { useModalChrome } from "@/components/ui/Modal";
import { Button } from "@/components/ui/primitives";

/* The whitelabel's KycIdentityVerificationModal: opened when the platform
   refuses a withdrawal with KYC_REQUIRED, it says why and offers the one
   step that clears it. It closes on navigation, the way the whitelabel's
   does, since the verify action may send the player to Profile Details. */
export function KycModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panel = useModalChrome(open, onClose);
  const { verifyIdentity, isPending } = useVerifyIdentityAction({
    onPrerequisiteMissing: onClose,
    onAlreadyVerified: onClose,
  });

  const pathname = usePathname();
  const opened = useRef(pathname);
  useEffect(() => {
    if (!open) {
      opened.current = pathname;
      return;
    }
    if (pathname !== opened.current) onClose();
  }, [open, pathname, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-[2px]"
          />

          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby="kyc-modal-title"
            initial={{ opacity: 0, scale: 0.95, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="modal-shadow relative flex w-full max-w-[452px] flex-col items-center gap-[24px] rounded-2xl border border-border-input bg-elevation-1 p-[24px] pt-[48px]"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-[16px] top-[16px] grid h-[38px] w-[38px] place-items-center text-fg transition-colors hover:text-fg-muted"
            >
              <Icon name="close" size={20} />
            </button>

            <div className="flex flex-col items-center gap-[16px] text-center">
              <span className="grid h-[64px] w-[64px] place-items-center rounded-full bg-elevation-3 text-brand-fg-accent">
                <Icon name="verified" size={32} />
              </span>
              <div className="flex flex-col gap-[8px]">
                <h2 id="kyc-modal-title" className="text-[20px] font-bold leading-[1.3] text-fg">
                  Verify your identity
                </h2>
                <p className="text-[14px] leading-[1.6] text-fg-subtle">
                  To keep withdrawals available, you&apos;ll need to verify your identity. It only takes a few
                  minutes.
                </p>
              </div>
            </div>

            <Button onClick={verifyIdentity} disabled={isPending} className="w-full">
              {isPending ? "Opening…" : "Verify identity"}
            </Button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
